import React, { useMemo } from "react"
import { Header, flexRender } from "@tanstack/react-table"
import { useSortable } from "@dnd-kit/sortable"

import { cn } from "~/ui-rtk/utils/tailwind-utils"

import type {
  TableVisualizationColumnDef,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import { ArrowDownSvg, ArrowTopSvg } from "~/ui-rtk/components/ui/svg/arrow"
import { getCommonPinningOrderingStyles } from "../../utils"
import { PinSvg } from "~/ui-rtk/components/ui/svg/essentials"
import { MetricInfoIcon } from "~/ui-rtk/components/ui/common/MetricTooltip"
import { useMetricFromDictionary } from "~/ui-rtk/hooks/metric-dictionary"

type THeadCellProps = {
  header: Header<TMetric, unknown>
  className?: string
  sorting?: "asc" | "desc"
  sortingOrder?: number
  columnSizingInfo?: {
    isResizingColumn: string | false
    deltaOffset: number | null
  }
}

export const HeadCell: React.FC<THeadCellProps> = ({
  header,
  className,
  sorting,
  sortingOrder,
  columnSizingInfo,
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    })

  // Get metric key from column definition for tooltip
  const columnDef = header.column.columnDef as TableVisualizationColumnDef
  const metricKey = columnDef.metricKey
  const { metric } = useMetricFromDictionary(metricKey)

  const classNameWithAction = cn(
    "px-2 py-1.5 text-md text-white font-bold leading-6 tracking-[-2%] text-left group bg-basic-dark-blue hover:z-20 group",
    className,
    {
      "z-10": isDragging || header.column.getIsPinned(),
    },
  )

  const thStyles = useMemo(() => {
    const { minSize, percentSize } = header.column
      .columnDef as TableVisualizationColumnDef

    if (percentSize) {
      return {
        maxWidth: percentSize,
        minWidth: `${minSize ? `${minSize}px` : `${percentSize}`}`,
        width: percentSize,
        ...getCommonPinningOrderingStyles(header.column, isDragging, transform),
      }
    }
    return {
      maxWidth: `${header.column.getSize() ? `${header.column.getSize()}px` : "unset"}`,
      minWidth: `${header.column.getSize() ? `${header.column.getSize()}px` : "unset"}`,
      cursor: header.column.getCanSort() ? "pointer" : undefined,
      ...getCommonPinningOrderingStyles(header.column, isDragging, transform),
    }
  }, [header, isDragging, transform])

  // Calculate live width during resizing
  let liveWidth = header.getSize()
  let resizerTransform = ""
  if (
    columnSizingInfo &&
    columnSizingInfo.isResizingColumn === header.column.id &&
    typeof columnSizingInfo.deltaOffset === "number"
  ) {
    liveWidth = Math.max(30, liveWidth + columnSizingInfo.deltaOffset) // 30px min width
    // Only apply transform in 'onEnd' mode for the resizer handle
    const tableOptions = header.getContext().table.options
    if (tableOptions.columnResizeMode === "onEnd") {
      const dir = tableOptions.columnResizeDirection === "rtl" ? -1 : 1
      resizerTransform = `translateX(${dir * columnSizingInfo.deltaOffset}px)`
    }
  }

  return (
    <th
      key={header.id}
      style={{ ...thStyles, width: liveWidth ? `${liveWidth}px` : undefined }}
      className={`${classNameWithAction} relative`}
      colSpan={header.colSpan}
      ref={setNodeRef}
    >
      <div className="flex items-center justify-between gap-1">
        {header.column.getIsPinned() ? (
          <div onClick={header.column.getToggleSortingHandler()}>
            {header.isPlaceholder
              ? null
              : flexRender(header.column.columnDef.header, header.getContext())}
          </div>
        ) : (
          <div
            onClick={header.column.getToggleSortingHandler()}
            {...attributes}
            {...listeners}
          >
            {header.isPlaceholder
              ? null
              : flexRender(header.column.columnDef.header, header.getContext())}
          </div>
        )}
        <div className="flex items-center justify-end gap-0.5">
          {/* Metric info icon with tooltip */}
          {metric?.description && (
            <MetricInfoIcon
              metric={metric}
              size={12}
              place="bottom"
              className="opacity-60 group-hover:opacity-100"
            />
          )}
          {sorting && (
            <div className="whitespace-nowrap">
              {sorting === "desc" && <ArrowDownSvg size={14} />}
              {sorting === "asc" && <ArrowTopSvg size={14} />}
              {<sub className="text-xs">{(sortingOrder ?? 0) + 1}</sub>}
            </div>
          )}
          {!sorting && header.column.getCanSort() && (
            <div
              className="opacity-0 group-hover:opacity-100"
              onClick={header.column.getToggleSortingHandler()}
            >
              <ArrowDownSvg />
            </div>
          )}
          {header.column.getIsPinned() && (
            <PinSvg onClick={() => header.column.pin(false)} />
          )}
          {!header.column.getIsPinned() && header.column.getCanPin() && (
            <div className="opacity-0 group-hover:opacity-100">
              <PinSvg onClick={() => header.column.pin("left")} />
            </div>
          )}
        </div>
      </div>
      {header.column.getCanResize() && (
        <>
          {header.column.getIsResizing() && (
            <div
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "#1e293b",
                color: "#fff",
                fontSize: "12px",
                padding: "2px 6px",
                borderRadius: "4px",
                zIndex: 20,
                pointerEvents: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              {liveWidth}px
            </div>
          )}
          <div
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            className="resizer"
            style={{
              cursor: header.column.getIsResizing()
                ? "col-resize"
                : "ew-resize",
              userSelect: "none",
              position: "absolute",
              right: 0,
              top: 0,
              height: "100%",
              width: "6px",
              zIndex: 10,
              touchAction: "none",
              background: header.column.getIsResizing()
                ? "#60a5fa"
                : "transparent",
              transition: "background 0.2s",
              transform: resizerTransform,
            }}
          />
        </>
      )}
    </th>
  )
}
